// src/hooks/useTrafficData.js
import { useEffect, useState } from "react";
import { fetchToday, fetchMonth, fetchYear } from "../lib/api";
import { firstOfMonth } from "../lib/dates";

/**
 * Props:
 *  - day, month, year
 *  - bucket: "", "overnight" | "morning" | "afternoon" | "evening"
 *  - sunMode: null | 1 (Dusk–Dawn = night) | 0 (Dawn–Dusk = day)
 *  - plate: user-entered plate or "=ALIAS"
 */
export default function useTrafficData({ day, month, year, bucket, sunMode, plate }) {
  const [todayRows, setTodayRows] = useState([]);
  const [monthRows, setMonthRows] = useState([]);
  const [yearRows, setYearRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // periodic refresh (60s)
  useEffect(() => {
    const id = setInterval(() => setRefreshKey((k) => k + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        // sunMode -> API 'light' switch ("night" / "day"); when set, ignore bucket
        const light = (sunMode === 0 || sunMode === 1) ? (sunMode ? "night" : "day") : "";
        const filter = light ? { light } : { bucket };
        const base = { plate };

        const [a, b, c] = await Promise.all([
          fetchToday({ day, ...filter, ...base }),
          fetchMonth({ month: month || firstOfMonth(new Date()), ...filter, ...base }),
          fetchYear({ year, ...filter, ...base }),
        ]);

        if (!alive) return;
        setTodayRows(a?.rows || []);
        setMonthRows(b?.rows || []);
        setYearRows(c?.rows || []);
      } catch (e) {
        console.error("useTrafficData fetch error:", e);
        if (!alive) return;
        setTodayRows([]);
        setMonthRows([]);
        setYearRows([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [day, month, year, bucket, sunMode, plate, refreshKey]);

  return { todayRows, monthRows, yearRows, loading };
}
